import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import useIntersectionObserver, { UseIntersectionObserverOptions } from '@pflegia/ui/hooks/use-intersection-observer';

import { TrackingEventName } from '@shared/application/marketing/events/tracking-event-name';
import { trackEvent } from '@shared/application/tracking.service';

const OBSERVATION_DELAY_MS = 1000;

export interface ViewedTrackerProps {
  eventName: TrackingEventName;
  properties?: Record<string, unknown>;
  isFlagReady?: boolean;
  className?: string;
  children?: ReactNode;
}

const ViewedTracker: React.FC<ViewedTrackerProps> = ({ eventName, properties, isFlagReady = true, className, children }) => {
  const trackerRef = useRef<HTMLDivElement>(null);
  const hasFiredRef = useRef(false);
  const [observationReady, setObservationReady] = useState(false);

  const eventNameRef = useRef(eventName);
  const propertiesRef = useRef(properties);

  useEffect(() => {
    eventNameRef.current = eventName;
    propertiesRef.current = properties;
  });

  // Delay observation to avoid firing on initial render when the element is already in the viewport
  useEffect(() => {
    const timer = setTimeout(() => setObservationReady(true), OBSERVATION_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleIntersection = useCallback<UseIntersectionObserverOptions['callback']>(entries => {
    entries.forEach(entry => {
      if (entry.intersectionRatio > 0 && !hasFiredRef.current) {
        hasFiredRef.current = true;
        trackEvent(eventNameRef.current, propertiesRef.current);
      }
    });
  }, []);

  useIntersectionObserver({
    callback: handleIntersection,
    ref: trackerRef,
    condition: observationReady && isFlagReady,
  });

  return (
    <div ref={trackerRef} className={className}>
      {children}
    </div>
  );
};

export default ViewedTracker;
